import {Navigate, useLocation} from 'react-router-dom';
import {Spinner} from '@/components/ui/Spinner';
import {authApi} from '@/api/auth';
import {useAuth} from '@/contexts/AuthContext';
import {useState} from 'react';

interface RequireAuthProps {
    children: React.ReactNode;
}

function VerifyEmailScreen() {
    const {user, logout} = useAuth();
    const location = useLocation();
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');

    // Signing in by link verifies the address, so one email does both jobs.
    const handleResend = async () => {
        if (!user?.email) {
            return;
        }

        setSending(true);
        setError('');
        try {
            await authApi.sendSignInLink(user.email, `${location.pathname}${location.search}`);
            setSent(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to send the sign-in link');
        } finally {
            setSending(false);
        }
    };

    return (
        <div
            style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#F5F6F6',
                fontFamily: '\'Plus Jakarta Sans\', \'Inter\', -apple-system, BlinkMacSystemFont, sans-serif'
            }}
        >
            <div
                style={{
                    background: '#fff',
                    borderRadius: 14,
                    border: '1px solid #E5E9ED',
                    padding: '48px 40px',
                    maxWidth: 440,
                    width: '100%',
                    textAlign: 'center'
                }}
            >
                <div
                    style={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        background: 'rgba(239, 90, 43, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 24px'
                    }}
                >
                    <svg fill="none" height="24" viewBox="0 0 24 24" width="24">
                        <path d="M3 8L10.89 13.26C11.2187 13.4793 11.6049 13.5963 12 13.5963C12.3951 13.5963 12.7813 13.4793 13.11 13.26L21 8M5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V7C21 6.46957 20.7893 5.96086 20.4142 5.58579C20.0391 5.21071 19.5304 5 19 5H5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19Z" stroke="#EF5A2B" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
                    </svg>
                </div>

                <h2
                    style={{
                        fontSize: '2.2rem',
                        fontWeight: 700,
                        color: '#15171A',
                        margin: '0 0 8px 0',
                        letterSpacing: '-0.02em'
                    }}
                >
                    Verify your email
                </h2>

                <p
                    style={{
                        fontSize: '1.4rem',
                        color: '#7C8B9A',
                        margin: '0 0 32px 0',
                        lineHeight: 1.6
                    }}
                >
                    We sent a verification link to <strong style={{color: '#394047'}}>{user?.email}</strong>. Please check your inbox and click the link to continue.
                </p>

                {error && (
                    <p style={{fontSize: '1.3rem', color: '#F50B23', margin: '0 0 16px 0'}}>
                        {error}
                    </p>
                )}

                {sent ? (
                    <p style={{fontSize: '1.3rem', color: '#30CF43', margin: '0 0 24px 0', fontWeight: 500}}>
                        Verification email sent! Check your inbox.
                    </p>
                ) : (
                    <button disabled={sending}
                        style={{
                            height: 38,
                            padding: '0 20px',
                            borderRadius: 40,
                            background: '#EF5A2B',
                            color: '#fff',
                            fontSize: '1.35rem',
                            fontWeight: 500,
                            border: 'none',
                            cursor: sending ? 'not-allowed' : 'pointer',
                            opacity: sending ? 0.7 : 1,
                            transition: 'all 0.2s ease',
                            fontFamily: 'inherit',
                            marginBottom: 24
                        }}
                        type="button"
                        onClick={handleResend}
                    >
                        {sending ? 'Sending...' : 'Resend verification email'}
                    </button>
                )}

                <div style={{borderTop: '1px solid #EBEEF0', paddingTop: 20, marginTop: 8}}>
                    <button style={{
                        background: 'none',
                        border: 'none',
                        color: '#7C8B9A',
                        fontSize: '1.3rem',
                        cursor: 'pointer',
                        fontFamily: 'inherit'
                    }}
                    type="button"
                    onClick={logout}
                    >
                        Sign Out
                    </button>
                </div>
            </div>
        </div>
    );
}

export function RequireAuth({children}: RequireAuthProps) {
    const {isAuthenticated, isLoading, user} = useAuth();
    const location = useLocation();

    if (isLoading) {
        return (
            <div style={{minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff'}}>
                <Spinner />
            </div>
        );
    }

    if (!isAuthenticated) {
        const returnTo = `${location.pathname}${location.search}`;
        return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
    }

    if (user && !user.email_verified_at && location.pathname !== '/settings/delete-account') {
        return <VerifyEmailScreen />;
    }

    return <>{children}</>;
}
